import type { LBNodeData } from '@/proto/logiblocks/logiblocks';
import { NOTIFICATIONS_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import {
  collectErrors,
  validateRequiredArray,
  validateRequiredTemplate,
  validateTemplateChunks,
} from '../../validation';
import { GroupUsersIcon } from '@/assets/icons/icons';
import { TeamsNotificationCustomization } from './TeamsNotificationCustomization';
import { defaultNodeData } from '../../graphUtils';

/** Validation field names for the Teams notification. Shared with the UI. */
export const TeamsNotificationFields = {
  RECIPIENT_TEAMS: 'Recipient teams',
  MESSAGE_TEMPLATE: 'Message template',
} as const;

export const TeamsNotificationNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'teams_notification',

  category: NodeCategory.NOTIFICATIONS,

  label: 'Microsoft Teams',

  hasInput: true,

  getIcon: (size: number) => (
    <GroupUsersIcon size={size} color={NOTIFICATIONS_ICON_COLOR} />
  ),

  outputType: OutputType.SINGLE,

  getSubtitle: (data: LBNodeData, _graph: GraphData) => {
    const teamCount = data.teams_notification?.recipient_team_ids?.length || 0;
    if (teamCount === 0) return 'To: (no teams selected)';
    return `To: ${teamCount} team${teamCount === 1 ? '' : 's'}`;
  },

  getValidationErrors: (
    nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => {
    const obj = nodeData.teams_notification;
    return collectErrors(
      validateRequiredArray(
        obj?.recipient_team_ids,
        TeamsNotificationFields.RECIPIENT_TEAMS
      ),
      validateRequiredTemplate(
        obj?.message_chunks ?? [],
        TeamsNotificationFields.MESSAGE_TEMPLATE
      ),
      ...validateTemplateChunks(
        obj?.message_chunks ?? [],
        nodeId,
        graph,
        TeamsNotificationFields.MESSAGE_TEMPLATE
      )
    );
  },

  CustomizationComponent: TeamsNotificationCustomization,

  createDefaultNodeData: () =>
    defaultNodeData('teams_notification', {
      message_chunks: [],
      recipient_team_ids: [],
      frequency: 'REALTIME', //just added in V1, all are optional
      day: '',
      time1: '',
      time2: '',
    }),

  showCustomizePanelOnCreation: true,
};
